import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  Animated,
  Platform,
  StyleSheet,
  Image,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { FontFamily } from '../../../constants/fonts';
import { AdminColors } from '../../Admin/constants/adminColors';
import { useDarkStyles, useDarkColor } from '../../../theme/useDarkStyles';

// ---- Types ------------------------------------------------------------------

export interface HarborTileData {
  id: string;
  title: string;
  description: string;
  roles: string;
  iconBg: string;
  iconBg2: string;
  Icon: React.FC;
  screen: string;
  screenParams?: { [key: string]: any };
}

interface HarborActionTileProps {
  tile: HarborTileData;
  onNavigate: (screen: string, params?: { [key: string]: any }) => void;
  isMobile?: boolean;
}

// ---- Arrow icon -------------------------------------------------------------

const ChevronRightIcon: React.FC<{ hovered: boolean }> = ({ hovered }) => {
  const dc = useDarkColor();
  return (
    <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
      <Path
        d="M9.29 6.71a1 1 0 000 1.41L13.17 12l-3.88 3.88a1 1 0 001.41 1.41l4.59-4.59a1 1 0 000-1.41L10.7 6.7a1 1 0 00-1.41.01z"
        fill={hovered ? '#ffffff' : dc(AdminColors.blue700)}
      />
    </Svg>
  );
};

// ---- Component --------------------------------------------------------------

const HarborActionTile: React.FC<HarborActionTileProps> = ({ tile, onNavigate, isMobile = false }) => {
  const styles = useDarkStyles(baseStyles);
  const [hovered, setHovered] = useState(false);
  const arrowTranslate = useRef(new Animated.Value(0)).current;

  const setHoverState = (next: boolean) => {
    setHovered(next);
    Animated.timing(arrowTranslate, {
      toValue: next ? 1 : 0,
      duration: 160,
      useNativeDriver: true,
    }).start();
  };

  const hoverProps =
    Platform.OS === 'web'
      ? {
          // @ts-ignore – web-only
          onMouseEnter: () => setHoverState(true),
          onMouseLeave: () => setHoverState(false),
        }
      : {};

  return (
    <Pressable
      onPress={() => onNavigate(tile.screen, tile.screenParams)}
      style={[styles.tileCard, hovered ? styles.tileCardHovered : styles.tileCardDefault, isMobile && styles.tileCardMobile]}
      accessibilityRole="button"
      accessibilityLabel={`Navigate to ${tile.title}`}
      {...hoverProps}
    >
      {/* Icon */}
      <View style={styles.tileIconWrap}>
        <tile.Icon />
      </View>

      {/* Title row */}
      <View style={styles.tileTitleRow}>
        <Text style={styles.tileTitle}>{tile.title}</Text>
      </View>

      {/* Description */}
      <Text style={styles.tileDescription}>{tile.description}</Text>

      {/* Footer: roles + arrow */}
      <View style={styles.tileFooter}>
        <Text style={styles.tileRoles}>{tile.roles}</Text>

        <Animated.View
          style={{
            transform: [
              {
                translateX: arrowTranslate.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, 3],
                }),
              },
            ],
          }}
        >
          <View style={[styles.tileArrowCircle, hovered && styles.tileArrowCircleHovered]}>
            <ChevronRightIcon hovered={hovered} />
          </View>
        </Animated.View>
      </View>
    </Pressable>
  );
};

// ---- Styles -----------------------------------------------------------------

const baseStyles = StyleSheet.create({
  tileCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 26,
    borderWidth: 2,
    flexDirection: 'column',
    gap: 12,
    minWidth: 300,
    maxWidth: '50%',
    flex: 1,
    ...Platform.select({
      ios: {
        shadowColor: '#0055BC',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.08,
        shadowRadius: 8,
      },
      android: { elevation: 2 },
    }),
  },
  // In a column layout, tiles should fill the full width instead of flex-growing.
  tileCardMobile: {
    flex: undefined,
    minWidth: undefined,
    maxWidth: '100%',
    width: '100%',
  },
  tileCardDefault: {
    borderColor: 'transparent',
  },
  tileCardHovered: {
    borderColor: AdminColors.blue700,
    transform: [{ translateY: -2 }],
    ...Platform.select({
      ios: { shadowOpacity: 0.15, shadowRadius: 12 },
      android: { elevation: 8 },
    }),
  },
  tileIconWrap: {
    height: 58,
    alignItems: 'flex-start',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  tileTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  tileTitle: {
    fontSize: 20,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#2D2D2D',
    flex: 1,
  },
  tileDescription: {
    fontSize: 14,
    fontFamily: FontFamily.regular,
    color: '#616161',
    lineHeight: 20,
    flex: 1,
  },
  tileFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  tileRoles: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
    color: '#616161',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    flex: 1,
    marginRight: 8,
  },
  tileArrowCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: AdminColors.blue700,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileArrowCircleHovered: {
    backgroundColor: AdminColors.blue700,
    borderColor: AdminColors.blue700,
  },
});

export default HarborActionTile;
